import { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase, Review } from '../../lib/supabase';
import AuthModal from '../auth/AuthModal';

interface ReviewSectionProps {
  partnerId: string;
  partnerName: string;
}

export default function ReviewSection({ partnerId, partnerName }: ReviewSectionProps) {
  const { user } = useAuth();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadReviews();
  }, [partnerId]);

  const loadReviews = async () => {
    try {
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .eq('partner_id', partnerId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('리뷰 로드 오류:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!user) {
      setIsAuthModalOpen(true);
      return;
    }

    setSubmitting(true);

    try {
      const { error } = await supabase
        .from('reviews')
        .insert([
          {
            user_id: user.id,
            partner_id: partnerId,
            partner_name: partnerName,
            rating,
            comment,
            user_name: user.user_metadata?.name || user.email || '익명'
          }
        ]);

      if (error) throw error;

      setComment('');
      setRating(5);
      setShowReviewForm(false);
      loadReviews(); // 리뷰 목록 새로고침
    } catch (error) {
      console.error('리뷰 저장 오류:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating: number, interactive = false, onRatingChange?: (rating: number) => void) => {
    return Array.from({ length: 5 }, (_, i) => (
      <button
        key={i}
        type={interactive ? 'button' : undefined}
        onClick={interactive && onRatingChange ? () => onRatingChange(i + 1) : undefined}
        className={`${interactive ? 'cursor-pointer hover:scale-110' : ''} transition-transform`}
        disabled={!interactive}
      >
        <i
          className={`ri-star-${i < rating ? 'fill' : 'line'} text-yellow-400 text-lg`}
        ></i>
      </button>
    ));
  };

  const averageRating = reviews.length > 0 
    ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length 
    : 0;

  return (
    <>
      <div className="bg-white rounded-2xl shadow-lg p-8 mt-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">리뷰 및 평점</h3>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                {renderStars(Math.round(averageRating))}
                <span className="text-lg font-semibold text-gray-700">
                  {averageRating.toFixed(1)}
                </span>
              </div>
              <span className="text-gray-500">({reviews.length}개 리뷰)</span>
            </div>
          </div>
          
          <button
            onClick={() => {
              if (!user) {
                setIsAuthModalOpen(true);
              } else {
                setShowReviewForm(!showReviewForm);
              }
            }}
            className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white rounded-xl font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line mr-2"></i>
            리뷰 작성
          </button>
        </div>

        {/* 리뷰 작성 폼 */}
        {showReviewForm && user && (
          <div className="bg-gray-50 rounded-xl p-6 mb-8">
            <h4 className="text-lg font-bold text-gray-800 mb-4">리뷰 작성하기</h4>
            <form onSubmit={handleSubmitReview} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  평점 *
                </label>
                <div className="flex items-center gap-2">
                  {renderStars(rating, true, setRating)}
                  <span className="ml-2 text-gray-600">({rating}점)</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  리뷰 내용 *
                </label>
                <textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  required
                  rows={4}
                  maxLength={500}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm resize-none"
                  placeholder="체험 후기를 자세히 작성해주세요 (최대 500자)"
                />
                <div className="text-right text-sm text-gray-500 mt-1">
                  {comment.length}/500
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="submit"
                  disabled={submitting || !comment.trim()}
                  className="px-6 py-3 bg-green-500 hover:bg-green-600 disabled:bg-gray-300 text-white rounded-xl font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  {submitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      등록 중...
                    </>
                  ) : (
                    '리뷰 등록'
                  )}
                </button>
                <button
                  type="button"
                  onClick={() => setShowReviewForm(false)}
                  className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors cursor-pointer whitespace-nowrap"
                >
                  취소
                </button>
              </div>
            </form>
          </div>
        )}

        {/* 리뷰 목록 */}
        <div className="space-y-6">
          {loading ? (
            <div className="text-center py-8">
              <i className="ri-loader-4-line animate-spin text-2xl text-gray-400 mb-2"></i>
              <p className="text-gray-500">리뷰를 불러오는 중...</p>
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12">
              <i className="ri-star-line text-6xl text-gray-300 mb-4"></i>
              <p className="text-gray-500 text-lg">아직 작성된 리뷰가 없습니다.</p>
              <p className="text-gray-400 text-sm">첫 번째 리뷰를 작성해보세요!</p>
            </div>
          ) : (
            reviews.map((review) => (
              <div key={review.id} className="border-b border-gray-200 pb-6 last:border-b-0">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <div className="flex items-center gap-3 mb-2">
                      <span className="font-semibold text-gray-800">{review.user_name}</span>
                      <div className="flex items-center gap-1">
                        {renderStars(review.rating)}
                      </div>
                    </div>
                    <p className="text-gray-600 text-sm">
                      {new Date(review.created_at).toLocaleDateString('ko-KR', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                <p className="text-gray-700 leading-relaxed">{review.comment}</p>
              </div>
            ))
          )}
        </div>
      </div>

      {/* 인증 모달 */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        defaultMode="login"
      />
    </>
  );
}